import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types/Spot';
import { DocumentSubCategory, DocumentUploadCategory, SUBCATEGORY_LABELS } from 'types/Documents';
import { getDocumentsByCategory } from 'utils/documents';
import { useDocuments } from 'contexts/DocumentsContext';
import { ModalRef } from 'components/Modal/Modal';
import AGMessage from 'components/AGMessage/AGMessage';
import Button from 'components/Button/Button';
import ArrowRightIcon from 'components/Icons/ArrowRightIcon';
import W9Item from './ItemByCategory/W9';
import HistoricalFinancialItem from './ItemByCategory/HistoricalFinancial';
import MonthlyRentRollItem from './ItemByCategory/MonthlyRentRoll';
import MapAndBluePrintItem from './ItemByCategory/MapAndBluePrint';
import EntryExitPhotosItem from './ItemByCategory/EntryExitPhotos';
import { BlueprintInfo, EntryExitInfo, SecurityInfo } from './utils';
import styles from './NewOwnerOnboarding.module.css';

interface NewOwnerOnboardingProps {
    spot: SpotInterface;
    navigateWithinOrg: (path: string) => void;
    setOnboardingMode: (mode: boolean) => void;
}

function NewOwnerOnboarding({ spot, navigateWithinOrg, setOnboardingMode }: NewOwnerOnboardingProps): JSX.Element {
    const { uploadDocuments, documentCategories, getDocuments, documents, errorMessage, loading } = useDocuments();

    const {
        w9: w9Category,
        historicalFinancial: historicalFinancialCategory,
        monthlyRentRoll: monthlyRentRollCategory,
        mapAndBluePrint: mapAndBluePrintCategory,
        entryExitPhotos: entryExitPhotosCategory,
    } = documentCategories;

    const documentsByCategory = useMemo(() => getDocumentsByCategory(documents), [documents]);

    const entryExitModalRef = useRef<ModalRef>(null);
    const blueprintModalRef = useRef<ModalRef>(null);

    const uploadNewDocuments = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>, category: DocumentUploadCategory) => {
            if (e.target.files?.length) {
                await uploadDocuments(spot.uuid, Array.from(e.target.files), category);
                e.target.files = null;
                trackEvent('Onboarding Document Uploaded', {
                    type: category.sub_category ? SUBCATEGORY_LABELS[category.sub_category] : category.category,
                    spotName: spot.name,
                    spot: spot.pk,
                });
            }
        },
        [uploadDocuments, spot]
    );

    function handleAddYourTeam() {
        trackEvent('Add Your Team', { spotName: spot.name, spot: spot.pk });
        navigateWithinOrg('/team');
    }

    function skipToDashboard() {
        trackEvent('Skip to Dashboard', { spotName: spot.name, spot: spot.pk });
        setOnboardingMode(false);
    }

    useEffect(() => {
        trackEvent('Onboarding Document Flow Viewed', { spotName: spot.name, spot: spot.pk });
    }, []);

    useEffect(() => {
        if (spot.uuid && documents === null) getDocuments(spot.uuid);
    }, [getDocuments, spot, documents]);

    return (
        <div className={styles.container}>
            <h1 className={styles.welcomeHeading}>Welcome to AirGarage</h1>
            <h2 className={styles.spotHeading}>Let&apos;s get {spot.name} earning</h2>
            <p className={styles.subDescription}>
                A few details and we&apos;ll handle the rest, such as payment collection, signage, and enforcement.
            </p>

            {errorMessage && <AGMessage color="error" title={errorMessage} />}

            <div className={`${styles.itemsList} ${loading ? styles.loading : ''}`}>
                <SecurityInfo />
                <W9Item
                    w9Documents={documentsByCategory[DocumentSubCategory.W9]}
                    category={w9Category}
                    uploadDocuments={uploadNewDocuments}
                    disableInteractions={loading}
                />
                <HistoricalFinancialItem
                    historicalFinancialDocuments={documentsByCategory[DocumentSubCategory.REVENUE_REPORT]}
                    category={historicalFinancialCategory}
                    uploadDocuments={uploadNewDocuments}
                    disableInteractions={loading}
                />
                <MonthlyRentRollItem
                    monthlyRentRollDocuments={documentsByCategory[DocumentSubCategory.MONTHLY_RENT_ROLL]}
                    category={monthlyRentRollCategory}
                    uploadDocuments={uploadNewDocuments}
                    disableInteractions={loading}
                />
                <MapAndBluePrintItem
                    mapAndBluePrintDocuments={documentsByCategory[DocumentSubCategory.PROPERTY_MAP]}
                    category={mapAndBluePrintCategory}
                    uploadDocuments={uploadNewDocuments}
                    infoRef={blueprintModalRef}
                    disableInteractions={loading}
                />
                <EntryExitPhotosItem
                    entryExitPhotosDocuments={documentsByCategory[DocumentSubCategory.PHOTO]}
                    category={entryExitPhotosCategory}
                    uploadDocuments={uploadNewDocuments}
                    infoRef={entryExitModalRef}
                    disableInteractions={loading}
                />
            </div>

            {documentsByCategory[DocumentSubCategory.W9].length > 0 && (
                <div className={styles.optionsContainer}>
                    <div className={styles.addTeamBanner}>
                        <div>
                            <p>You sent the required documents</p>
                            <span>Add your team so the right people can manage this property with you.</span>
                        </div>
                        <Button size="sm" color="info" onClick={handleAddYourTeam}>
                            Add Your Team <ArrowRightIcon />
                        </Button>
                    </div>
                    <button className={styles.skipButton} onClick={skipToDashboard}>
                        Skip to Dashboard <ArrowRightIcon />
                    </button>
                </div>
            )}

            <EntryExitInfo ref={entryExitModalRef} />
            <BlueprintInfo ref={blueprintModalRef} />
        </div>
    );
}

export default NewOwnerOnboarding;
